// BoardCard.js - Anslagstavla på startsidan (Spår A)
//
// Delad tavla per klinik: alla på kliniken sätter upp lappar, författaren tar
// bort sina egna, klinik-admin tar bort alla och fäster lappar överst.
// Hämtas vid sidladdning och var 60:e sekund så att kollegornas lappar syns.
// Texten renderas som ren text (React escapar), radbrytningar bevaras via CSS.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../apiClient';
import ConfirmDialog from '../common/ConfirmDialog';
import './BoardCard.css';

const POLL_INTERVAL_MS = 60000;
const MAX_TEXT_LENGTH = 1000;

const formatNoteTime = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  const now = new Date();
  const time = d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return `idag ${time}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  const date = d.toLocaleDateString('sv-SE', sameYear
    ? { day: 'numeric', month: 'short' }
    : { day: 'numeric', month: 'short', year: 'numeric' });
  return `${date} ${time}`;
};

const BoardCard = () => {
  const [notes, setNotes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const mountedRef = useRef(true);
  // Synkron vakt mot dubbelpost (t.ex. nedhållen Ctrl+Enter före omrendering)
  const savingRef = useRef(false);

  const loadNotes = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/board/notes');
      if (mountedRef.current) {
        setNotes(res.data?.notes || []);
        setLoaded(true);
      }
    } catch (err) {
      // Tavlan är inte kritisk - behåll senast hämtade lappar vid fel
      console.error('Kunde inte hämta anslagstavlan:', err);
      if (mountedRef.current) setLoaded(true);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadNotes();
    const timer = setInterval(loadNotes, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(timer);
    };
  }, [loadNotes]);

  const trimmed = text.trim();
  const tooLong = trimmed.length > MAX_TEXT_LENGTH;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trimmed || tooLong || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      await apiClient.post('/api/board/notes', { text: trimmed });
      setText('');
      await loadNotes();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte sätta upp lappen');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const togglePin = async (note) => {
    setError('');
    try {
      await apiClient.post(`/api/board/notes/${note.id}/${note.pinned ? 'unpin' : 'pin'}`);
      await loadNotes();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte ändra fästning');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError('');
    try {
      await apiClient.delete(`/api/board/notes/${deleteTarget.id}`);
      await loadNotes();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte ta bort lappen');
    } finally {
      setDeleteTarget(null);
      setDeleting(false);
    }
  };

  return (
    <div className="dashboard-card board-card">
      <div className="dashboard-card-header">
        <h2>
          <i className="fas fa-thumbtack board-card-icon" aria-hidden="true"></i> Anslagstavla
        </h2>
      </div>

      <form className="board-form" onSubmit={handleSubmit}>
        <textarea
          className="board-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Skriv en lapp till kollegorna…"
          rows={2}
          aria-label="Ny lapp på anslagstavlan"
          onKeyDown={(e) => {
            // Ctrl/Cmd+Enter sätter upp lappen, vanlig Enter ger radbrytning
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.repeat) handleSubmit(e);
          }}
        />
        <div className="board-form-footer">
          <span className={`board-counter${tooLong ? ' over' : ''}`}>
            {trimmed.length > MAX_TEXT_LENGTH - 200 ? `${trimmed.length}/${MAX_TEXT_LENGTH}` : ''}
          </span>
          <button
            type="submit"
            className="btn-base btn-primary btn-sm"
            disabled={!trimmed || tooLong || saving}
          >
            {saving ? 'Sätter upp…' : 'Sätt upp'}
          </button>
        </div>
      </form>

      {error && <p className="board-error">{error}</p>}

      {loaded && notes.length === 0 && (
        <p className="dashboard-empty">Inga lappar uppsatta.</p>
      )}

      {notes.length > 0 && (
        <ul className="board-list">
          {notes.map((note) => (
            <li key={note.id} className={`board-note${note.pinned ? ' pinned' : ''}`}>
              {note.pinned && (
                <span className="board-pinned-tag">
                  <i className="fas fa-thumbtack" aria-hidden="true"></i> Fäst
                </span>
              )}
              <p className="board-note-text">{note.text}</p>
              <div className="board-note-meta">
                <span>{note.author_name} · {formatNoteTime(note.created_at)}</span>
                <span className="board-note-actions">
                  {note.can_pin && (
                    <button
                      type="button"
                      className="dashboard-link"
                      onClick={() => togglePin(note)}
                    >
                      {note.pinned ? 'Lossa' : 'Fäst'}
                    </button>
                  )}
                  {note.can_delete && (
                    <button
                      type="button"
                      className="dashboard-link board-delete"
                      onClick={() => setDeleteTarget(note)}
                    >
                      Ta bort
                    </button>
                  )}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        variant="danger"
        title="Ta bort lapp?"
        message="Lappen tas bort från anslagstavlan för alla på kliniken."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default BoardCard;
